
import { computed, defineComponent, PropType, reactive } from 'vue'

// components
import Cheap from '@/common/components/UI/Cheap/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// composable
import useAccount from '@/common/composable/useAccount'
import { FtlLogistBid } from '@/core/types/ftl_bids.types'
import { useFile } from '@/common/composable/useFile'
import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        Cheap,
        ButtonDropdown,
        TheButton,
    },
    props: {
        logistBid: {
            type: Object as PropType<FtlLogistBid>,
            default: null,
        },
        statusId: {
            type: Number,
            default: null,
        },
        statusCode: {
            type: String,
            default: null,
        },
        statusName: {
            type: String,
            default: null,
        },
        ftlType: {
            type: Number,
            default: null,
        },
        bidId: {
            type: Number,
            default: null,
        },
        orderId: {
            type: Number,
            default: null,
        },
        isStatusChanging: {
            type: Boolean,
            default: false,
        },
        isDocumentLoading: {
            type: Boolean,
            default: false,
        },
        isDownloadLoading: {
            type: Boolean,
            default: false,
        },
        isEnterCostToBePaidLoading: {
            type: Boolean,
            default: false,
        },
        isEnterCarrierDataLoading: {
            type: Boolean,
            default: false,
        },
        isEnterCostPriceLoading: {
            type: Boolean,
            default: false,
        },
        isEnterCommentLoading: {
            type: Boolean,
            default: false,
        },
        isInvoicesPrinting: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'accept-in-work',
        'enter-cost-to-be-paid',
        'repeat-calculate-cost',
        'enter-carrier-data',
        'enter-cost-price',
        'enter-comment',
        'cancel-bid',
        'print-invoices',
        'document',
        'download',
    ],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
        })

        const ftlBidStatusCheapClass = computed(() => {
            return {
                'blue--outline': props.statusCode === 'created',
                'yellow--outline': props.statusCode === 'wait_cost_price',
                yellow:
                    props.statusCode === 'on_updating' ||
                    props.statusCode === 'on_the_way' ||
                    props.statusCode === 'on_inspection_head_transport',
                'orange--outline': props.statusCode === 'on_approval',
                'orange--fill': props.statusCode === 'carrier_data_entered',
                'yellow--fill': props.statusCode === 'approved',
                'green--outline': props.statusCode === 'completed',
                green: props.statusCode === 'confirmed',
                'red--outline': props.statusCode === 'canceled',
            }
        })

        const isBidStatusOnApproval = computed(() => props.statusId === 7)
        const isBidStatusCreated = computed(() => props.statusId === 5)
        const isBidStatusWaitCostPrice = computed(() => props.statusId === 1)
        const isBidStatusApproved = computed(() => props.statusId === 3)
        const isBidStatusCarrierDataEntered = computed(() => props.statusId === 8)
        const isBidStatusCanceled = computed(() => props.statusId === 4)

        const isCanAcceptInWork = computed(
            () =>
                isBidStatusCreated.value &&
                havePermissions(['cabinet-ftl-bid-update', 'all-root-permission'])
        )
        const isCanEnterCostToBePaid = computed(
            () =>
                isBidStatusOnApproval.value &&
                havePermissions(['cabinet-ftl-save-paid-price', 'all-root-permission'])
        )
        const isCanRepeatCalculateCost = computed(
            () =>
                isBidStatusOnApproval.value &&
                havePermissions(['cabinet-ftl-bid-change-status', 'all-root-permission'])
        )
        const isCanEnterCarrierData = computed(
            () =>
                isBidStatusApproved.value &&
                havePermissions(['cabinet-ftl-save-carrier_data', 'all-root-permission'])
        )
        const isCanCreateOrder = computed(
            () =>
                !props.orderId &&
                isBidStatusCarrierDataEntered.value &&
                havePermissions(['cabinet-ftl-bid-create', 'all-root-permission'])
        )
        const isCanEnterCostPrice = computed(
            () =>
                isBidStatusWaitCostPrice.value &&
                havePermissions(['cabinet-ftl-set-cost-price', 'all-root-permission'])
        )
        const isCanEnterComment = computed(() => isBidStatusWaitCostPrice.value)
        const isCanUpdateFtlBid = computed(() =>
            havePermissions(['cabinet-ftl-bid-update', 'all-root-permission'])
        )
        const isCanCancelFtlBid = computed(() =>
            havePermissions(['cabinet-ftl-bid-update', 'all-root-permission'])
        )
        const isLogistBid = computed(() => props.logistBid && props.logistBid.id)
        const isD24 = computed(() => props.logistBid && !props.logistBid.is_sended_to_contract)

        const onAcceptInWork = () => {
            emit('accept-in-work')
        }

        const onEnterCostToBePaid = () => {
            emit('enter-cost-to-be-paid')
        }

        const onRepeatCalculateCost = () => {
            emit('repeat-calculate-cost')
        }

        const onEnterCarrierData = () => {
            emit('enter-carrier-data')
        }

        const enterCarrierDataText = computed(() => {
            if ([0, 1].includes(props.ftlType)) {
                return 'Создать заявку логиста'
            }

            return 'Заполнить данные о перевозчике'
        })

        const onEnterCostPrice = () => {
            emit('enter-cost-price')
        }

        const onEnterComment = () => {
            emit('enter-comment')
        }

        const onCancelBid = () => {
            emit('cancel-bid')
        }

        const onPrintInvoices = () => {
            emit('print-invoices')
        }

        const emitD24 = () => emit('document')
        const emitDownload = () => emit('download')

        const getOrderLink = computed(() => {
            return props.ftlType === 2
                ? `/ftl/intracity/${props.orderId}`
                : `/ftl-orders/${props.orderId}`
        })

        const printLogistBid = async () => {
            if (state.is_loading) return

            try {
                state.is_loading = true

                const response = await ftl_bids.printLogistBid(props.bidId)
                saveFile(response, `Шаблон международной заявки логиста ${props.bidId}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При скачивании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            ftlBidStatusCheapClass,
            isCanAcceptInWork,
            onAcceptInWork,
            isCanEnterCostToBePaid,
            onEnterCostToBePaid,
            isCanRepeatCalculateCost,
            onRepeatCalculateCost,
            isCanEnterCarrierData,
            onEnterCarrierData,
            enterCarrierDataText,
            isCanCreateOrder,
            isCanEnterCostPrice,
            onEnterCostPrice,
            isCanEnterComment,
            onEnterComment,
            isCanUpdateFtlBid,
            isCanCancelFtlBid,
            onCancelBid,
            onPrintInvoices,
            isLogistBid,
            isD24,
            emitD24,
            emitDownload,
            getOrderLink,
            printLogistBid,
        }
    },
})
